<template>
  <div class="latest-news-container">
    <div v-if="inProgress" class="loading-state">
      <div class="custom-loader">
        <div class="loader-circle"/>
        <div class="loader-circle"/>
        <div class="loader-circle"/>
        <div class="loader-circle"/>
      </div>
      <p class="loading-text">Loading...</p>
    </div>
    <div v-else class="news-content">
      <div v-if="articles?.length" class="news-list modern-scrollbar">
        <div v-for="article in articles" :key="article.id" class="news-item" @click="viewArticle(article)">
          <div class="news-meta">
            <span class="news-date">{{ formatDate(article.publish_Date) }}</span>
            <span v-if="article?.authors?.length" class="meta-separator">|</span>
            <span v-if="article?.authors?.length" class="news-authors">
              <span v-for="author in article.authors" :key="author.name" class="author">
                <a :href="getAuthorUrl(author.name)" class="author-link" target="_blank" @click.stop>{{ author.name }}</a>
              </span>
            </span>
          </div>
          <h6 class="news-title">{{ article.name }}</h6>
          <div class="news-description" v-html="article.description" />
        </div>
        <div v-if="PUBLIC_ARTICLES.hasMore" class="load-more-wrap">
          <button
            class="load-more-btn"
            :disabled="PUBLIC_ARTICLES.loading"
            @click.stop="loadMorePublicArticles"
          >
            {{ PUBLIC_ARTICLES.loading ? 'Loading…' : `Load more (${PUBLIC_ARTICLES.count - PUBLIC_ARTICLES.data.length} remaining)` }}
          </button>
        </div>
      </div>
      <div v-else class="no-news">
        <p>No news to display.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import {
  getPublicArticles,
  loadMorePublicArticles,
  PUBLIC_ARTICLES,
  inProgress as articlesInProgress
} from "../../composables/profiles/articles";
import {
  SUBSCRIPTION_INFO,
  inProgress as subscriptionInProgress
} from "../../composables/subscription";
import pbDomain from "../../utils/pbDomain"

let data = ref([]);

const inProgress = computed(() => articlesInProgress.value || subscriptionInProgress.value);

const articles = computed(() => {
  let stringArray = SUBSCRIPTION_INFO.elements.map((r) => r.code);
  if (!stringArray.length) {
    const parentPackages = SUBSCRIPTION_INFO.parentSubscriptionPackages ?? [];
    for (let i = 0; i < parentPackages.length; i++) {
      const elements = parentPackages[i].elements ?? [];
      for (let j = 0; j < elements.length; j++) {
        stringArray.push(elements[j]);
      }
    }
  }
  data.value = (PUBLIC_ARTICLES.value?.data ?? []).filter((r) => {
    for (let i = 0; i < r.elements.length; i++) {
      if (stringArray.includes(r.elements[i])) {
        return r.type !== "REPORT";
      }
    }
  });
  return data.value.sort((a, b) => {
    const dateA = new Date(a.publish_Date).getTime();
    const dateB = new Date(b.publish_Date).getTime();
    return dateB - dateA;
  });
});

const formatDate = (dateStr) => {
  if (!dateStr) return "Unknown Date";
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "Unknown Date";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  } catch {
    return "Unknown Date";
  }
};

const getAuthorUrl = (authorName) => {
  if (!authorName) return "#";
  return `${pbDomain}/about-us?people=${authorName.replaceAll(" ", "_")}`;
};

const viewArticle = (nth) => {
  const baseUrl = `${pbDomain}/blue`;
  const slug = nth.name?.replaceAll(" ", "-").toLowerCase();

  let url = "";
  switch (nth.type) {
    case "NEWS":
      url = `${baseUrl}/news-analysis/${nth.id}/${slug}`;
      break;
    case "OPINION":
      url = `${baseUrl}/opinion-pieces/${nth.id}/${slug}`;
      break;
    case "REPORT":
    case "INSIGHT":
      url = `${baseUrl}/reports/${nth.id}/${slug}`;
      break;
    case "VIDEO":
      url = `${baseUrl}/videos/${nth.id}`;
      break;
    default:
      url = `${baseUrl}/news-analysis/${nth.id}/${slug}`;
  }
  window.open(url, "_blank");
};

onMounted(async () => {
  if (!PUBLIC_ARTICLES.value.data.length) await getPublicArticles();
});
</script>

<style lang="scss" scoped>
.latest-news-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.loading-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1;
  width: 100%;
  gap: 1.5rem;
}

.custom-loader {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.loader-circle {
  width: 12px;
  height: 12px;
  background-color: #00213b;
  border-radius: 50%;
  animation: loader-bounce 1.4s ease-in-out infinite both;

  &:nth-child(1) { animation-delay: -0.32s; }
  &:nth-child(2) { animation-delay: -0.16s; }
  &:nth-child(3) { animation-delay: 0s; }
  &:nth-child(4) { animation-delay: 0.16s; }
}

@keyframes loader-bounce {
  0%, 80%, 100% { transform: scale(0); opacity: 0.3; }
  40%           { transform: scale(1); opacity: 1;   }
}

.loading-text {
  color: #00213b;
  font-size: 1rem;
  font-weight: 500;
  margin: 0;
  opacity: 0.8;
}

.news-content {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.news-list {
  height: 100%;
  overflow-y: auto;
  padding: 0 1rem;
}

.load-more-wrap {
  padding: 0.75rem 0;
  text-align: center;
}

.load-more-btn {
  font-size: 0.75rem;
  font-weight: 600;
  color: #213ce5;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.news-item {
  padding: 1rem 0;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
    margin: 0 -1rem;
    padding: 1rem;
  }

  &:last-child {
    border-bottom: none;
  }
}

.news-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.6875rem;
  color: #718096;
}

.news-date {
  font-weight: 500;
}

.meta-separator {
  opacity: 0.6;
}

.news-authors {
  display: flex;
  gap: 0.25rem;
}

.author {
  &:not(:first-child)::before {
    content: "• ";
    margin-right: 0.25rem;
  }
}

.author-link {
  color: #718096;
  text-decoration: none;
  transition: color 0.2s;

  &:hover {
    color: #2184ff;
  }
}

.news-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #000;
  margin: 0 0 0.5rem 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  &:hover{
    color: #2184ff;
  }
}

.news-description {
  font-size: 0.75rem !important;
  color: #000;
  line-height: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  :deep(p) {
    margin: 0;
    font-size: 0.75rem !important;
    background-color: transparent !important;
  }
  :deep(span){
    font-size: 0.75rem !important;
    background-color: transparent !important;
  }
}

.no-news {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;

  p {
    color: #718096;
    font-size: 0.9rem;
    margin: 0;
  }
}
</style>
